import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ApiError, api, type Category, type PostSummary } from './api.js';
import { buildMonthGrid, isoDateLocal, monthBounds } from './calendar-grid.js';

const LIBELLES_STATUT: Record<PostSummary['status'], string> = {
  draft: 'Brouillon',
  generated: 'Généré',
  pending_review: 'À valider',
  approved: 'Programmé',
  rejected: 'Écarté',
  published: 'Publié',
  failed: 'Échec',
};

const JOURS = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.'];

function heure(iso: string | null): string {
  return iso ? new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
}

export function Calendar({ csrfToken }: { csrfToken: string | undefined }) {
  const [moisAffiche, setMoisAffiche] = useState(() => {
    const maintenant = new Date();
    return new Date(maintenant.getFullYear(), maintenant.getMonth(), 1, 12);
  });
  const [posts, setPosts] = useState<PostSummary[] | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);

  async function rafraichir() {
    setErreur(null);
    try {
      const bornes = monthBounds(moisAffiche);
      const [{ posts: p }, { categories: c }] = await Promise.all([api.posts(bornes), api.categories()]);
      setPosts(p);
      setCategories(c);
    } catch (error) {
      setErreur(error instanceof ApiError ? error.message : 'Chargement impossible.');
    }
  }

  useEffect(() => {
    void rafraichir();
  }, [moisAffiche]);

  const grille = useMemo(() => buildMonthGrid(moisAffiche), [moisAffiche]);
  const postsParJour = useMemo(() => {
    const index = new Map<string, PostSummary[]>();
    for (const post of posts ?? []) {
      if (!post.scheduledFor) continue;
      const cle = isoDateLocal(new Date(post.scheduledFor));
      index.set(cle, [...(index.get(cle) ?? []), post]);
    }
    return index;
  }, [posts]);

  const maintenant = new Date();
  const aVenir = (posts ?? [])
    .filter((post) => post.scheduledFor && new Date(post.scheduledFor) >= maintenant)
    .slice(0, 3);

  const quotas = categories
    .filter((category) => category.monthlyQuota > 0)
    .map((category) => {
      const compte = (posts ?? []).filter((post) => post.category.id === category.id && post.status !== 'rejected').length;
      return { category, compte, taux: Math.min(compte / category.monthlyQuota, 1) };
    })
    .sort((a, b) => b.taux - a.taux)
    .slice(0, 5);

  function changerMois(delta: number) {
    setMoisAffiche((date) => new Date(date.getFullYear(), date.getMonth() + delta, 1, 12));
  }

  return (
    <div className="page-calendrier">
      {erreur ? <p className="erreur" role="alert">{erreur}</p> : null}

      <div className="calendrier-layout">
        <section className="calendrier-carte" aria-label="Calendrier éditorial">
          <header className="calendrier-outils">
            <div className="navigation-mois">
              <button type="button" className="bouton-icone" onClick={() => changerMois(-1)} aria-label="Mois précédent">‹</button>
              <button type="button" className="bouton-icone" onClick={() => changerMois(1)} aria-label="Mois suivant">›</button>
              <h2>{moisAffiche.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</h2>
            </div>
            <div className="legende-calendrier" aria-label="Légende">
              {(['published', 'approved', 'pending_review', 'rejected'] as const).map((status) => (
                <span key={status}><i data-statut={status} />{LIBELLES_STATUT[status]}</span>
              ))}
            </div>
          </header>

          <div className="jours-semaine">{JOURS.map((jour) => <span key={jour}>{jour}</span>)}</div>
          <div className="grille-calendrier">
            {grille.map((jour) => {
              const postsDuJour = postsParJour.get(jour.iso) ?? [];
              const aujourdHui = jour.iso === isoDateLocal(maintenant);
              return (
                <div key={jour.iso} className={`case-calendrier${jour.inCurrentMonth ? '' : ' hors-mois'}`}>
                  <span className={aujourdHui ? 'numero-jour aujourd-hui' : 'numero-jour'}>{jour.date.getDate()}</span>
                  {postsDuJour.slice(0, 2).map((post) => (
                    <article className="post-calendrier" key={post.id} title={post.topic.title}>
                      {post.mediaId ? post.mediaKind === 'video' ? <video src={`/api/media/${post.mediaId}`} muted playsInline preload="metadata" /> : <img src={`/api/media/${post.mediaId}`} alt="" /> : <span className="miniature-vide" />}
                      <span className="heure-post"><i data-statut={post.status} />{heure(post.scheduledFor)}</span>
                      <strong>{post.topic.title}</strong>
                    </article>
                  ))}
                  {postsDuJour.length > 2 ? <small className="posts-supplementaires">+ {postsDuJour.length - 2}</small> : null}
                </div>
              );
            })}
          </div>
        </section>

        <aside className="colonne-calendrier">
          <section className="panneau-lateral">
            <header><h2>À venir</h2><span>{aVenir.length} publication{aVenir.length > 1 ? 's' : ''}</span></header>
            {posts === null ? <p className="sous-titre">Chargement…</p> : aVenir.length === 0 ? <p className="texte-vide">Aucune publication à venir ce mois-ci.</p> : (
              <div className="liste-a-venir">
                {aVenir.map((post) => (
                  <article key={post.id}>
                    {post.mediaId ? post.mediaKind === 'video' ? <video src={`/api/media/${post.mediaId}`} muted playsInline preload="metadata" /> : <img src={`/api/media/${post.mediaId}`} alt="" /> : <span className="vignette-compacte-vide" />}
                    <div><span className="badge-statut" data-statut={post.status}>{LIBELLES_STATUT[post.status]}</span><small>{post.scheduledFor ? new Date(post.scheduledFor).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : ''} · {heure(post.scheduledFor)}</small><strong>{post.topic.title}</strong></div>
                  </article>
                ))}
              </div>
            )}
          </section>

          <section className="panneau-lateral rythme-mois">
            <header><h2>Plafonds du mois</h2></header>
            <p>Repères maximums, jamais des objectifs à remplir. Une actualité ne se publie que si elle mérite votre validation.</p>
            {quotas.length === 0 ? <p className="texte-vide">Aucun plafond configuré.</p> : quotas.map(({ category, compte, taux }) => (
              <div className="ligne-quota" key={category.id}>
                <div><span>{category.name}</span><small>{compte} / {category.monthlyQuota}</small></div>
                <span className="barre-quota"><i style={{ width: `${Math.max(taux * 100, 3)}%` }} data-complet={taux >= 1} /></span>
              </div>
            ))}
          </section>
        </aside>
      </div>

      <NouveauSujet categories={categories} csrfToken={csrfToken} onCree={() => void rafraichir()} />
    </div>
  );
}

function NouveauSujet({ categories, csrfToken, onCree }: { categories: Category[]; csrfToken: string | undefined; onCree: () => void }) {
  const [categoryId, setCategoryId] = useState('');
  const [titre, setTitre] = useState('');
  const [donnees, setDonnees] = useState<Record<string, string>>({});
  const [envoi, setEnvoi] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [echec, setEchec] = useState(false);

  const categorie = categories.find((category) => category.id === categoryId);
  const attendues = Object.entries(categorie?.expectedVariables ?? {});

  async function soumettre(event: FormEvent) {
    event.preventDefault();
    setEnvoi(true);
    setMessage(null);
    setEchec(false);
    try {
      const remplies = Object.fromEntries(Object.entries(donnees).filter(([, valeur]) => valeur.trim() !== ''));
      await api.createTopic(categoryId, titre, remplies, csrfToken);
      setTitre('');
      setDonnees({});
      setMessage('Sujet ajouté. Il est prêt à être rédigé dans « Sujets & veille ».');
      onCree();
    } catch (error) {
      setEchec(true);
      setMessage(error instanceof ApiError ? error.message : 'Création impossible.');
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <section className="nouveau-sujet" id="nouveau-sujet">
      <div className="intro-formulaire"><p className="surtitre">Création manuelle</p><h2>Nouveau sujet</h2><p>Le titre lance le travail. Les données factuelles empêchent l’IA d’inventer ce qu’elle ne sait pas.</p></div>
      <form onSubmit={soumettre}>
        <div className="champs-principaux">
          <div><label htmlFor="categorie">Catégorie</label><select id="categorie" required value={categoryId} onChange={(event) => { setCategoryId(event.target.value); setDonnees({}); }}><option value="" disabled>Choisir une catégorie</option>{categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select></div>
          <div><label htmlFor="titre-sujet">Sujet</label><input id="titre-sujet" required maxLength={300} value={titre} onChange={(event) => setTitre(event.target.value)} placeholder="Loi Carrez, maison à Casteljaloux, réforme du DPE…" /></div>
        </div>

        {attendues.length > 0 ? (
          <fieldset className="donnees-sujet">
            <legend>Données factuelles</legend>
            <p>Sans ces éléments, la rédaction sera refusée plutôt que de produire un chiffre incertain.</p>
            <div className="grille-donnees">{attendues.map(([cle, exemple]) => <div key={cle}><label htmlFor={`var-${cle}`}>{cle}</label><input id={`var-${cle}`} value={donnees[cle] ?? ''} placeholder={exemple} onChange={(event) => setDonnees((actuelles) => ({ ...actuelles, [cle]: event.target.value }))} /></div>)}</div>
          </fieldset>
        ) : null}

        {message ? <p className={echec ? 'erreur' : 'succes'} role={echec ? 'alert' : 'status'}>{message}</p> : null}
        <div className="actions-formulaire"><button type="submit" disabled={envoi || !categoryId || !titre}>{envoi ? 'Ajout…' : 'Ajouter aux sujets'}</button></div>
      </form>
    </section>
  );
}
